import type {
  IHttpCall,
  IHttpRequest,
  IHttpResponse,
} from '@hc-frontend/core-utils-net';
import { fetchBasedCall } from '@hc-frontend/core-utils-net';

import {
  FetchCallAvailabilityPayloadDTO,
  FetchCallAvailabilityResponseDTO,
} from './fetch-call-availability.types';

type FetchCallAvailabilityOptions = {
  /** Aborts the request after this many milliseconds. */
  timeoutMs?: number;
  httpCall?: IHttpCall;
};

/**
 * Asks leads-api whether any Boberdoo buyer can take a call for the session's
 * lead on the given ring pool (MP-4244). The check pings Boberdoo in
 * `Mode=ping`, so it creates no lead and triggers no delivery.
 *
 * Server-side only. Rejects when the request is aborted by `timeoutMs` or the
 * payload fails validation; callers decide how to treat that.
 *
 * @param baseUrl - Base URL of leads-api.
 * @param payload - Session, ring pool and page context for the check.
 * @param options - Optional timeout and HTTP transport override.
 * @returns The raw HTTP response with the availability `status`.
 */
export function fetchCallAvailability(
  baseUrl: string,
  payload: FetchCallAvailabilityPayloadDTO,
  options?: FetchCallAvailabilityOptions,
): Promise<IHttpResponse<FetchCallAvailabilityResponseDTO>> {
  const call = options?.httpCall ?? fetchBasedCall;
  const timeoutMs = options?.timeoutMs;

  const request: IHttpRequest<FetchCallAvailabilityPayloadDTO> = {
    url: `${baseUrl}/api/v1/call/campaign/availability`,
    method: 'POST',
    body: payload,
    // Availability changes with buyer hours and caps; never reuse a result.
    cache: 'no-store',
    ...(timeoutMs ? { signal: AbortSignal.timeout(timeoutMs) } : {}),
    schemas: {
      request: FetchCallAvailabilityPayloadDTO,
      response: FetchCallAvailabilityResponseDTO,
    },
  };

  return call(request);
}
